import {
    GroupQueueEntry,
    useQueueTimeContext,
} from './useQueueTimeContext'

import {
    NinchatProperties,
    NinchatStore,
} from 'api/types'

import {
    getAcceptedAudienceIds,
    isPendingTicket,
} from './ticketState'
import {getActiveQueueSettings} from 'utils/queueSettings'
import {hasLoadedComments} from './ticketDurationData'
import {useAppSelectorWithShallowEquality} from '../index'

export type TicketDuration = {
    isLoaded?: boolean
    queue_time: NinchatProperties.Time
    queueLength: number
    sourceQueueId?: NinchatProperties.QueueID
}

type TicketDurationOptions = {
    assignOldestOnly?: boolean
    audienceComments: NinchatStore.NinchatUserAudienceCommentsStore
    audienceTranscripts: NinchatStore.NinchatAudienceTranscriptsStore
    groupQueues: GroupQueueEntry[]
    newOnly?: boolean
    parentGroupQueue?: NinchatProperties.NinchatUserQueue
    queue: NinchatProperties.NinchatUserQueue | undefined
    queueActivities: NinchatStore.NinchatUserQueuesActivityStore
    queueId: NinchatProperties.QueueID
}

type UseTicketDurationOptions = {
    assignOldestOnly?: boolean
    newOnly?: boolean
}

type OldestPendingTicketTimeOptions = {
    audienceTranscripts: NinchatStore.NinchatAudienceTranscriptsStore
    comments: NinchatProperties.NinchatAudienceRangeComments | undefined
    newOnly?: boolean
}

type OldestPendingTicketTime = {
    isLoaded: boolean
    queue_time: NinchatProperties.Time
}

const getOldestPendingTicketTime = (
    activities: NinchatProperties.NinchatQueueActivity[] | undefined,
    options: OldestPendingTicketTimeOptions,
): OldestPendingTicketTime => {
    const acceptedAudienceIds = getAcceptedAudienceIds(activities)
    const completedActivities = [...activities || []]
        .filter((activity) => activity.event === 'audience_complete' && activity.audience_id)
        .sort((a, b) => a.time - b.time)

    for (const activity of completedActivities) {
        const audienceId = activity.audience_id as NinchatProperties.AudienceID
        const comments = options.comments?.[audienceId]
        const transcript = options.audienceTranscripts[audienceId]

        if (!transcript && !hasLoadedComments(options.comments, audienceId)) {
            return {
                isLoaded: false,
                queue_time: 0,
            }
        }

        if (isPendingTicket({
            acceptedAudienceIds,
            activity,
            comments,
            newOnly: options.newOnly,
            transcript,
        })) {
            return {
                isLoaded: true,
                queue_time: activity.time,
            }
        }
    }

    return {
        isLoaded: true,
        queue_time: 0,
    }
}

export const getTicketDuration = ({
    audienceComments,
    assignOldestOnly,
    audienceTranscripts,
    groupQueues,
    newOnly,
    parentGroupQueue,
    queue,
    queueActivities,
    queueId,
}: TicketDurationOptions): TicketDuration => {
    if (!queue) {
        return {
            isLoaded: true,
            queueLength: 0,
            queue_time: 0,
        }
    }

    if (!queue.queue_attrs.is_group) {
        const queueSettings = getActiveQueueSettings({
            parentGroupQueue,
            queue,
        })
        const assignOldestDisabled = assignOldestOnly && !queueSettings?.assign_oldest_ticket

        if (!queueSettings?.audience_ticketing || assignOldestDisabled) {
            return {
                isLoaded: true,
                queueLength: 0,
                queue_time: 0,
            }
        }

        const queueLength = queue.queue_attrs.unassigned_registered ?? 0
        const activities = queueActivities[queueId]
        const oldestPendingTicketTime = queueLength
            ? getOldestPendingTicketTime(activities, {
                audienceTranscripts,
                comments: audienceComments[queueId],
                newOnly,
            })
            : undefined
        const hasLoadedActivity = activities !== undefined && oldestPendingTicketTime?.isLoaded === true
        const isLoaded = queueLength === 0 || hasLoadedActivity

        return {
            isLoaded,
            queueLength,
            queue_time: oldestPendingTicketTime?.queue_time ?? 0,
        }
    }

    return groupQueues.reduce<TicketDuration>((current, child) => {
        const activeSettings = getActiveQueueSettings({
            parentGroupQueue: queue,
            queue: child.queueProps,
        })
        const assignOldestDisabled = assignOldestOnly && !activeSettings?.assign_oldest_ticket

        if (!activeSettings?.audience_ticketing || assignOldestDisabled) {
            return current
        }

        const groupQueueLength = child.queueProps.queue_attrs.unassigned_registered ?? 0

        if (groupQueueLength === 0) {
            return current
        }

        const activities = queueActivities[child.id]

        if (activities === undefined) {
            return {
                isLoaded: false,
                queueLength: current.queueLength + groupQueueLength,
                queue_time: current.queue_time,
            }
        }

        const groupQueueTime = getOldestPendingTicketTime(
            activities,
            {
                audienceTranscripts,
                comments: audienceComments[child.id],
                newOnly,
            },
        )

        return {
            isLoaded: current.isLoaded === true && groupQueueTime.isLoaded,
            queueLength: current.queueLength + groupQueueLength,
            queue_time: groupQueueTime.queue_time && (!current.queue_time || groupQueueTime.queue_time < current.queue_time)
                ? groupQueueTime.queue_time
                : current.queue_time,
            sourceQueueId: groupQueueTime.queue_time && (!current.queue_time || groupQueueTime.queue_time < current.queue_time)
                ? child.id
                : current.sourceQueueId,
        }
    }, {
        isLoaded: true,
        queueLength: 0,
        queue_time: 0,
    })
}

export const useTicketDuration = (
    queue_id: NinchatProperties.QueueID,
    options: UseTicketDurationOptions = {},
): TicketDuration => {
    const {
        groupQueues,
        parentGroupQueue,
        queue,
    } = useQueueTimeContext(queue_id)

    return useAppSelectorWithShallowEquality((state) => getTicketDuration({
        assignOldestOnly: options.assignOldestOnly,
        audienceComments: state.ninchat._user_audience_comments,
        audienceTranscripts: {
            ...state.ninchat._audience_transcripts,
            ...state.ninchat._user_audience_transcripts,
        },
        groupQueues,
        newOnly: options.newOnly,
        parentGroupQueue,
        queue,
        queueActivities: state.ninchat._user_queues_activity,
        queueId: queue_id,
    }))
}
